import {
  Card,
  ECardBorderRadiusSize,
  ETextStyleVariant,
  SkeletonLine,
  Text,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import escapeHtml from 'escape-html';
import React, { AnchorHTMLAttributes } from 'react';

import Anchor from '@/components/switchback/Anchor/Anchor';
import Link from '@/components/switchback/Link/Link';
import { IWishlistingEventData } from '@/services/analytics/listings/types';
import { TAutoRentalTile } from '@/utility/mapAutoSearchResultToTile';
import { ISearchResultTile } from '@/utility/mapSearchResultToTile';

import { ListingTileContent } from './ListingTileContent';
import { ListingTileMedia } from './ListingTileMedia';
import { ListingTileMediaActions } from './ListingTileMediaActions';
import { computeCustomListingTitle } from './utils';

export enum EListingTileVariant {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

type TListingTileProps = Pick<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'target' | 'onClick' | 'onMouseEnter' | 'onMouseLeave'
> & {
  variant?: EListingTileVariant;
  isTransparent?: boolean;
  isBordered?: boolean;
  isOutlined?: boolean;
  isPromoCard?: boolean;
  promoTitle?: string;
  listingTile: ISearchResultTile | TAutoRentalTile;
  withPriority?: boolean;
  withFetchPriority?: 'high' | 'low' | undefined;
  showFirstImageOnly?: boolean;
  highQualityImages?: boolean;
  onSlideChange?: (index: number) => void;
  wishlistingEventData?: IWishlistingEventData;
  showFavoriteToggleAnimation?: boolean;
  hideTagsOnSmallScreen?: boolean;
  showNewListingTag?: boolean;
  showGuestFavoriteTag?: boolean;
  showSuperhostBadge?: boolean;
  mediaClassName?: string;
  customContent?: React.ReactNode;
  showProBadge?: boolean;
  forceShowTotalPrice?: boolean;
  className?: string;
  /**
   * For certain pages (for example locality pages) we have in place nginx configuration that
   * rewrite rules and does not allow us using client side redirects. Use it to selectively enable
   * client side routing for desired pages.
   */
  forceClientsideRouting?: boolean;
  dataTestId?: string;
};

export const ListingTile = ({
  variant = EListingTileVariant.Vertical,
  isTransparent = true,
  isBordered = false,
  isOutlined = false,
  isPromoCard,
  promoTitle,
  listingTile,
  withPriority,
  withFetchPriority,
  showFirstImageOnly,
  highQualityImages,
  onSlideChange,
  wishlistingEventData,
  showFavoriteToggleAnimation = true,
  hideTagsOnSmallScreen,
  showNewListingTag,
  showGuestFavoriteTag,
  showSuperhostBadge,
  mediaClassName = '',
  customContent,
  showProBadge,
  forceShowTotalPrice,
  className = '',
  forceClientsideRouting,
  target,
  onClick,
  onMouseEnter,
  onMouseLeave,
  dataTestId,
}: TListingTileProps) => {
  const computedCustomTitle = computeCustomListingTitle(listingTile);
  const escapedTitle = computedCustomTitle || (listingTile.title && escapeHtml(listingTile.title));

  const isVertical = variant === EListingTileVariant.Vertical;
  const isHorizontal = variant === EListingTileVariant.Horizontal;
  const isContained = isBordered || !isTransparent;

  let card = (
    <Card
      showHoverEffect={false}
      withBoxShadow={!isTransparent}
      withTransparentBackground={isTransparent}
      borderRadiusSize={ECardBorderRadiusSize.Large}
      className={cn(className, {
        // When is outlined and not transparent the whole card will have a green outline
        'outline outline-2 outline-offset-2 outline-green-500': isOutlined && isContained,
      })}>
      <div className={cn('rounded-[inherit]', { flex: isHorizontal })}>
        <ListingTileMedia
          listingTile={listingTile}
          withPriority={withPriority}
          withFetchPriority={withFetchPriority}
          showFirstImageOnly={showFirstImageOnly}
          highQuality={highQualityImages}
          onSlideChange={onSlideChange}
          actionsLayer={
            <ListingTileMediaActions
              listingTile={listingTile}
              wishlistingEventData={wishlistingEventData}
              showFavoriteToggleAnimation={showFavoriteToggleAnimation}
              hideTagsOnSmallScreen={hideTagsOnSmallScreen}
              showNewListingTag={showNewListingTag}
              showLimitedOfferTag={isPromoCard}
              showGuestFavoriteTag={showGuestFavoriteTag}
              showSuperhostBadge={showSuperhostBadge}
            />
          }
          className={cn('overflow-clip', mediaClassName, {
            // Rounded border
            'rounded-[inherit]': !isContained,
            'rounded-t-[inherit] rounded-b-none': isVertical && isContained,
            'rounded-l-[inherit] rounded-r-none': isHorizontal && isContained,

            // Aspect ratio & size
            'aspect-[4/3] w-full': isVertical && !isContained,
            'aspect-[4/2] w-full': isVertical && isContained,
            'aspect-[4/3] w-1/2 flex-shrink-0': isHorizontal,

            // When is outlined and transparent the media will have a green outline
            'outline outline-2 outline-offset-2 outline-green-500': isOutlined && !isContained,
          })}
        />

        <div
          className={cn({
            'py-4': isVertical && !isContained,
            'px-4 py-4': isVertical && isContained,
            'p-4 flex-grow': isHorizontal,
            'border border-gray-900': isBordered,
            'border-t-0 rounded-b-[inherit]': isVertical && isBordered,
            'border-l-0 rounded-r-[inherit]': isHorizontal && isContained,
          })}>
          {customContent || (
            <ListingTileContent
              listingTile={listingTile}
              showProBadge={showProBadge}
              forceShowTotalPrice={forceShowTotalPrice}
              customTitle={isPromoCard ? promoTitle : undefined}
              secondaryInfo={
                // Show only the location for the promo cards
                isPromoCard && promoTitle ? (
                  <>
                    {(listingTile.loading && (
                      <Text component="div" variant={ETextStyleVariant.LargeRegular}>
                        <SkeletonLine width="60%" className="bg-gray-200" />
                      </Text>
                    )) ||
                      (listingTile.details?.location && (
                        <Text component="div" variant={ETextStyleVariant.LargeRegular}>
                          {listingTile.details.location}
                        </Text>
                      ))}
                  </>
                ) : undefined
              }
            />
          )}
        </div>
      </div>
    </Card>
  );

  if (isPromoCard) {
    card = <div className="p-4 bg-green-30 rounded-2xl">{card}</div>;
  }

  if (listingTile.linkUrl) {
    return (
      <Link
        href={listingTile.linkUrl}
        forceClientsideRouting={forceClientsideRouting}
        legacy
        passHref>
        <Anchor
          data-testid={dataTestId}
          aria-label={isPromoCard ? promoTitle : escapedTitle}
          title={isPromoCard ? promoTitle : escapedTitle}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          href={listingTile.linkUrl}
          target={target}
          referrer-policy="no-referrer-when-downgrade"
          rel="noopener">
          {card}
        </Anchor>
      </Link>
    );
  }

  return card;
};
